import React from "react";
import InputField from '../../../components/inputs/inputField';
import ColorField from "../../../components/inputs/colorField";
import FileUploadInputBox from "../../../components/inputs/fileUploadInputBox";
import FilesUploaderModal from "../../../components/files/filesUploaderModal";
import API from '../../../utils/API';

class CheckinSetup extends React.Component {
	constructor(props) {
		super(props);
		this.state = {
			uploadingAppIcon: "",
		};
	}

	resizeImageToBlob = (imageURL, targetWidth, targetHeight, fit = 'cover', onComplete, onError) => {
		const img = new Image();
		img.crossOrigin = 'anonymous';

		img.onload = () => {
			const canvas = document.createElement('canvas');
			canvas.width = targetWidth;
			canvas.height = targetHeight;

			const ctx = canvas.getContext('2d');
			if (!ctx) {
				onError(new Error(`No canvas context for ${targetWidth}x${targetHeight}`));
				return;
			}

			const scale = fit === 'contain'
				? Math.min(targetWidth / img.width, targetHeight / img.height)
				: Math.max(targetWidth / img.width, targetHeight / img.height);
			const x = (targetWidth - img.width * scale) / 2;
			const y = (targetHeight - img.height * scale) / 2;

			ctx.clearRect(0, 0, targetWidth, targetHeight);
			ctx.drawImage(img, x, y, img.width * scale, img.height * scale);

			canvas.toBlob((blob) => {
				if (!blob) {
					onError(new Error(`Failed to create blob for ${targetWidth}x${targetHeight}`));
					return;
				}
				onComplete(blob);
			}, 'image/webp', 0.92);
		};

		img.onerror = () => {
			onError(new Error(`Failed to load image for ${targetWidth}x${targetHeight}`));
		};
		img.src = imageURL;
	};

	uploadResizedIconBlob = (blob, fileName, onComplete, onError) => {
		const data = new FormData();
		data.append('targetDirectory', '');
		data.append('files', blob, fileName);

		API()
			.post(`Organizations/${this.props.orgId}/events/${this.props.eventId}/assets`, data, {
				headers: {
					'Content-Type': 'multipart/form-data'
				}
			})
			.then((res) => {
				const uploadedUrl = res?.data?.assets?.[0]?.url;
				if (!uploadedUrl) {
					onError(new Error(`Upload succeeded but URL missing for ${fileName}`));
					return;
				}
				onComplete(uploadedUrl);
			})
			.catch((error) => {
				onError(error);
			});
	};

	buildResizedAppIcons = (imageURL, onComplete, onError) => {
		if (!imageURL) {
			onComplete({ small: '', medium: '', large: '' });
			return;
		}

		let completed = 0;
		let failed = 0;
		const results = {};

		const handleComplete = () => {
			if (completed + failed === 3) {
				onComplete(results);
			}
		};

		const handleResizeSuccess = (size) => {
			return (uploadedUrl) => {
				results[size] = uploadedUrl;
				completed++;
				handleComplete();
			};
		};

		const handleError = (size) => {
			return (error) => {
				results[size] = imageURL;
				failed++;
				handleComplete();
			};
		};

		const startResizeAndUpload = (size, width, height) => {
			this.resizeImageToBlob(
				imageURL,
				width,
				height,
				'cover',
				(blob) => {
					this.uploadResizedIconBlob(
						blob,
						`checkin-app-icon-${size}-${width}x${height}.webp`,
						handleResizeSuccess(size),
						handleError(size)
					);
				},
				handleError(size)
			);
		};

		startResizeAndUpload('small', 128, 128);
		startResizeAndUpload('medium', 256, 256);
		startResizeAndUpload('large', 512, 512);
	};

	applyBrandingToStyling = () => {
		const eventBranding = this.props.event.appConfiguration || {};

		this.props.handleChange({
			backgroundColor: eventBranding.backgroundColor,
			themeColor: eventBranding.themeColor,
			brandColor: eventBranding.brandColor,
			buttonTextColor: eventBranding.buttonTextColor,
			icons: {
				small: eventBranding.icons?.small || "",
				medium: eventBranding.icons?.medium || "",
				large: eventBranding.icons?.large || "",
			},
		});
	}

	render() {
		const appConfig = this.props.checkInAppConfiguration || {};
		const icons = appConfig.icons || { small: '', medium: '', large: '' };
		const readOnly = this.props.readOnly || false;
		const requiredFields = this.props.requiredFields || {};

		return (
			<div className="sectionBody">
				<div className='blocksContainer'>
					<div className='flex w-100'>
						<div className="sectionBlock mr-25 contained-h">
							<h2 className='blockHeader'>Application Details</h2>
							<InputField
								label="Name"
								value={appConfig.name}
								disabled={readOnly}
								required={true}
								errorTrigger={requiredFields.name}
								onChange={(e) => {
									this.props.handleChange({ name: e.target.value });
								}}
							/>
							<InputField
								label="Short Name"
								value={appConfig.shortName}
								disabled={readOnly}
								required={true}
								errorTrigger={requiredFields.shortName}
								onChange={(e) => {
									this.props.handleChange({ shortName: e.target.value });
								}}
							/>
							<div className='blockHeader flex aic jcsb'>
								<h2 className='m-0 blockHeader'> App Branding</h2>
								<button className="neu" onClick={this.applyBrandingToStyling}>
									Apply App Branding
								</button>
							</div>
							<ColorField
								label="Theme Color"
								value={appConfig.themeColor}
								type="color"
								useHex={true}
								disabled={readOnly}
								required={true}
								errorTrigger={requiredFields.themeColor}
								onChange={(e) => {
									this.props.handleChange({ themeColor: e });
								}}
							/>
							<h2 className='blockHeader'>Color Options</h2>
							<ColorField
								label="Brand Color"
								value={appConfig.brandColor}
								type="color"
								useHex={true}
								disabled={readOnly}
								required={true}
								errorTrigger={requiredFields.brandColor}
								onChange={(e) => {
									this.props.handleChange({ brandColor: e });
								}}
							/>

							<ColorField
								label="Button Text Color"
								value={appConfig.buttonTextColor}
								type="color"
								useHex={true}
								disabled={readOnly}
								required={true}
								errorTrigger={requiredFields.buttonTextColor}
								onChange={(e) => {
									this.props.handleChange({ buttonTextColor: e });
								}}
							/>
							<h2 className='blockHeader'>Application Icons</h2>
							<div className="flex aic gap20">
								<FileUploadInputBox
									label="(auto 128x128, 256x256, 512x512)"
									onClick={() => {
										this.setState({ uploadingAppIcon: "large" });
									}}
									value={icons.large}
									clearFile={() => {
										this.props.handleChange({
											icons: {
												...icons,
												small: "",
												medium: "",
												large: ""
											}
										});
										this.setState({ uploadingAppIcon: "" });
									}}
									addText="Icon"
								/>
							</div>
							{this.state.uploadingAppIcon !== "" && <FilesUploaderModal
								title="Icon"
								isOpen={this.state.uploadingAppIcon !== ""}
								toggle={() => this.setState({ uploadingAppIcon: "" })}
								eventId={this.props.eventId}
								programId={this.props.programId}
								links={{
									getAssets: !this.props.programId ? `Organizations/${this.props.orgId}/events/${this.props.eventId}/assets` : `Organizations/${this.props.orgId}/assets`,
									addAssets: !this.props.programId ? `Organizations/${this.props.orgId}/events/${this.props.eventId}/assets` : `Organizations/${this.props.orgId}/assets`,
								}}
								orgId={this.props.orgId}
								addFiles={(fileURL) => {
									this.buildResizedAppIcons(
										fileURL,
										(resizedIcons) => {
											this.props.handleChange({
												icons: {
													...icons,
													...resizedIcons
												}
											});
											this.setState({ uploadingAppIcon: "" });
										},
										(error) => {
											this.props.handleChange({
												icons: {
													...icons,
													small: fileURL,
													medium: fileURL,
													large: fileURL
												}
											});
											this.setState({ uploadingAppIcon: "" });
										}
									);
								}}
							/>}

						</div>
					</div>
				</div>
			</div>
		);
	}
}

export default CheckinSetup;